import { useMemo, useRef, useState, useEffect } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, Space, Table, Tag } from 'antd';
import type { InputRef, TableProps } from 'antd';
import Highlighter from 'react-highlight-words';
import type { Product } from '../../../../../models/Product/Product';
import { useCartContext } from '../../../../../context/CartContext/CartContext';
import {
  MdAdd as PlusIcon,
  MdRemove as DeleteIcon,
  MdLink as ViewIcon,
  MdSettingsBackupRestore as ResetPriceIcon,
  MdHourglassEmpty as LoadingIcon
} from 'react-icons/md';
import { toast } from 'react-toastify';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Product as ProductModel } from '../../../../../models/Product/Product';
import style from '../create-order.module.scss';
import { currencyFormat } from '../../../../../utils/helpers/helpers';
import cx from 'classnames';
import { ColumnType, ColumnsType } from 'antd/lib/table';
import { FilterValue, FilterConfirmProps } from 'antd/lib/table/interface';
import { ProductStatus } from '../../../../../models/Product/contracts';

interface DataType {
  key: number;
  sku: string;
  name: string;
  category: string;
  price: number;
  status: string;
  product: Product;
}

const ProductStatusClasses: { [key: string]: string } = {
  [ProductStatus.AVAILABLE]: 'cs-available-status',
  [ProductStatus.RENTED]: 'cs-rented-status',
  [ProductStatus.RESERVED]: 'cs-reserved-status',
  [ProductStatus.DAMAGED]: 'cs-damaged-status',
  [ProductStatus.LOST]: 'cs-lost-status',
  [ProductStatus.PENDING]: 'cs-pending-status',
};

const ProductStatusLabels: { [key: string]: string } = {
  [ProductStatus.AVAILABLE]: 'Dostupno',
  [ProductStatus.RENTED]: 'Izdato',
  [ProductStatus.RESERVED]: 'Rezervisano',
  [ProductStatus.DAMAGED]: 'Oštećeno',
  [ProductStatus.LOST]: 'Izgubljeno',
  [ProductStatus.PENDING]: 'Čeka na odobrenje',
};

const ProductsTable = ({ products }: { products: Product[] }) => {
  const { cart, cartAddProduct, cartRemoveProduct } = useCartContext();
  const selectedIdArray = useMemo(
    () => cart?.products.map((p) => p.id) ?? [],
    [cart?.products]
  );

  // --- Pagination State ---
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  // --- Global Search State ---
  const [searchInputValue, setSearchInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInputValue);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInputValue]);

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<InputRef>(null);
  const [activeFilters, setActiveFilters] = useState<
    Record<string, FilterValue | null>
  >({});
  const [resettingProductId, setResettingProductId] = useState<number | null>(
    null
  );
  const queryClient = useQueryClient();

  const { mutate: resetProductPrice, isLoading: isResettingPrice } =
    useMutation<Product, Error, number>(
      async (productId: number) => {
        setResettingProductId(productId);
        const updated = await ProductModel.resetPriceToCategory(productId);
        return updated;
      },
      {
        onSuccess: (updated) => {
          toast.success(
            `Cena za proizvod \"${updated.name}\" je uspešno resetovana!`
          );
          queryClient.invalidateQueries({ queryKey: ['products-for-order'] });
        },
        onError: (error: any) => {
          toast.error(
            error.response?.data?.message || 'Greška pri resetovanju cene.'
          );
        },
        onSettled: () => setResettingProductId(null)
      }
    );

  const handleSearch = (
    selectedKeys: string[],
    confirm: (options?: FilterConfirmProps) => void,
    dataIndex: keyof DataType
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (
    clearFilters: (() => void) | undefined,
    confirm: (options?: FilterConfirmProps) => void
  ) => {
    clearFilters?.();
    setSearchText('');
    confirm();
  };

  const getColumnSearchProps = (
    dataIndex: keyof DataType
  ): ColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters
    }) => (
      <div className={style.inputSearchWrapper} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Pretraži ${dataIndex}`}
          value={selectedKeys[0] as string}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Pretraži
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Resetuj
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => confirm({ closeDropdown: true })}
          >
            Zatvori
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    filteredValue: activeFilters[dataIndex] || null,
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text?.toString() ?? ''}
        />
      ) : (
        text
      )
  });

  const columns: ColumnsType<DataType> = [
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      ...getColumnSearchProps('sku'),
      render: (_, row) => (
        <div className={style.checkmarkColumn}>
          {selectedIdArray.includes(row.key) && <PlusIcon />}
          <span>{row.sku}</span>
        </div>
      )
    },
    {
      title: 'Naziv',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name')
    },
    {
      title: 'Kategorija',
      dataIndex: 'category',
      key: 'category',
      ...getColumnSearchProps('category')
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          className={cx(
            ProductStatusClasses[status] || 'cs-default-status',
            'cs-status-badge'
          )}
        >
          {ProductStatusLabels[status] || status}
        </Tag>
      )
    },
    {
      title: 'Cena (RSD)',
      dataIndex: 'price',
      key: 'price',
      align: 'right',
      render: (price) => currencyFormat(price)
    },
    {
      title: 'Akcije',
      key: 'actions',
      align: 'center',
      width: 150,
      render: (_, row) => {
        const isResetting =
          isResettingPrice && resettingProductId === row.product.id;
        const isSelected = selectedIdArray.includes(row.key);
        return (
          <div className={style.tableIcons}>
            <button
              type="button"
              className="cs-table-button-icon"
              onClick={() => cartAddProduct(row.product.id, 1)}
              disabled={isResetting || row.status === 'rented'}
              title="Dodaj u narudžbinu"
            >
              <PlusIcon />
            </button>
            <button
              type="button"
              className="cs-table-button-icon"
              onClick={() => cartRemoveProduct(row.product.id)}
              disabled={isResetting || !isSelected}
              title="Ukloni iz narudžbine"
            >
              <DeleteIcon />
            </button>
            <button
              type="button"
              className="cs-table-button-icon"
              onClick={() => resetProductPrice(row.product.id)}
              disabled={isResetting}
              title="Resetuj cenu na cenu kategorije"
            >
              {isResetting ? (
                <LoadingIcon className="animate-spin" />
              ) : (
                <ResetPriceIcon />
              )}
            </button>
            <a
              className="cs-table-button-icon"
              href={`/admin/product/${row.key}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Pogledaj proizvod"
              style={isResetting ? { pointerEvents: 'none', opacity: 0.5 } : {}}
            >
              <ViewIcon />
            </a>
          </div>
        );
      }
    }
  ];

  // --- Filtered Data ---
  const filteredData = useMemo(() => {
    if (!debouncedSearch) return products;
    const search = debouncedSearch.toLowerCase();
    return products.filter(
      (p) =>
        p.name?.toLowerCase().includes(search) ||
        p.sku?.toLowerCase().includes(search) ||
        p.category?.name?.toLowerCase().includes(search)
    );
  }, [products, debouncedSearch]);

  const data: DataType[] = useMemo(
    () =>
      filteredData.map((product) => ({
        key: product.id,
        sku: product.sku ?? 'N/A',
        name: product.name ?? 'N/A',
        category: product.category?.name ?? 'N/A',
        price: product.price ?? 0,
        status: product.status ?? 'available',
        product
      })),
    [filteredData]
  );

  // --- Table Change Handler ---
  const handleTableChange: TableProps<DataType>['onChange'] = (
    pag,
    filters,
    _sorter
  ) => {
    setActiveFilters(filters as Record<string, FilterValue>);
    setPagination({
      current: pag.current || 1,
      pageSize: pag.pageSize || 10
    });
    if (searchedColumn && !filters[searchedColumn]) {
      setSearchText('');
      setSearchedColumn('');
    }
  };

  // --- Reset All Filters Handler ---
  const handleResetAllFilters = () => {
    setActiveFilters({});
    setSearchText('');
    setSearchedColumn('');
    setSearchInputValue('');
  };

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Input
          placeholder="Opšta pretraga..."
          prefix={<SearchOutlined />}
          value={searchInputValue}
          onChange={(e) => setSearchInputValue(e.target.value)}
          style={{ width: 300 }}
        />
        <button className={style.resetButton} onClick={handleResetAllFilters} disabled={isResettingPrice}>
          Resetuj Sve Filtere
        </button>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="key"
        className={cx(
          style.tableWrapper,
          'cs-table-wrapper',
          'cs-create-order-table'
        )}
        scroll={{ y: 500, x: 'max-content' }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: data.length,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50', '100'],
          onChange: (page, pageSize) => setPagination({ current: page, pageSize })
        }}
        onChange={handleTableChange}
      />
    </>
  );
};

export default ProductsTable;
